import { Heading2 } from '@front/components/data-display/typography/Typography'
import { AppButton } from '@front/components/inputs/app-button'
import { DisplayRichText } from '@front/components/inputs/rich-text/DisplayRichText'
import type {
  Widget,
  TextWidget,
  ImageWidget,
  VideoWidget,
  ActionWidget,
} from '@front/type/career/career-pages.type'
import { Box, Stack, useTheme } from '@mui/material'

type WidgetProps = {
  widget: Widget
  buttonColor?: string
  textColor?: string
}

type TextWidgetProps = {
  widget: TextWidget
}

type ImageWidgetProps = {
  widget: ImageWidget
}

type ActionWidgetProps = {
  widget: ActionWidget
  buttonColor?: string
  textColor?: string
}

type VideoWidgetProps = {
  widget: VideoWidget
}

function TextWidget({ widget }: TextWidgetProps) {
  return (
    <Box sx={{ '& p': { m: 0 } }}>
      <DisplayRichText value={widget.text} />
    </Box>
  )
}

function ImageWidget({ widget }: ImageWidgetProps) {
  if (!widget.uri) {
    return null
  }

  return (
    <Box
      component="img"
      src={widget.uri}
      sx={{ width: '100%', objectFit: 'cover' }}
    />
  )
}

function ActionWidget({ widget, buttonColor, textColor }: ActionWidgetProps) {
  const theme = useTheme()

  const goToLink = () => {
    let url: URL
    try {
      url = new URL(widget.uri)
    } catch {
      return
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return
    }
    window.open(url.href, '_blank', 'noopener,noreferrer')
  }

  return (
    <AppButton
      fullWidth
      sx={{
        backgroundColor:
          buttonColor ?? theme.palette.action['primary-bg-default'],
        color: textColor ?? theme.palette.text['on-color'],
        border: 'none',
        '&:hover': {
          backgroundColor:
            buttonColor ?? theme.palette.action['primary-bg-default'],
          opacity: 0.5,
        },
      }}
      onClick={goToLink}
    >
      {widget.label}
    </AppButton>
  )
}

function toEmbedUrl(uri: string): string | null {
  try {
    const url = new URL(uri)
    if (url.hostname === 'youtu.be') {
      return `https://www.youtube.com/embed${url.pathname}`
    }
    if (url.hostname === 'www.youtube.com' || url.hostname === 'youtube.com') {
      const v = url.searchParams.get('v')
      if (v) {
        return `https://www.youtube.com/embed/${v}`
      }
      if (url.pathname.startsWith('/embed/')) {
        return url.toString()
      }
    }
  } catch {
    return null
  }
  return null
}

function VideoWidget({ widget }: VideoWidgetProps) {
  const embedUrl = toEmbedUrl(widget.uri)

  if (!embedUrl) {
    return null
  }

  return (
    <Stack sx={{ gap: 2 }}>
      <Heading2>{widget.title}</Heading2>
      <Box sx={{ width: '100%', aspectRatio: '16/9' }}>
        <iframe
          title={`embed-video-${widget.title}`}
          src={embedUrl}
          width="100%"
          height="100%"
          allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          style={{ border: 'none' }}
        />
      </Box>
    </Stack>
  )
}

export default function CareerWidget({
  widget,
  buttonColor,
  textColor,
}: WidgetProps) {
  const renderWidget = () => {
    switch (widget.type) {
      case 'text':
        return <TextWidget widget={widget.content} />
      case 'image':
        return <ImageWidget widget={widget.content} />
      case 'action':
        return (
          <ActionWidget
            widget={widget.content}
            buttonColor={buttonColor}
            textColor={textColor}
          />
        )
      case 'video':
        return <VideoWidget widget={widget.content} />
      default:
        return <></>
    }
  }

  return renderWidget()
}
